"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";

/**
 * 包含天数的倒计时时间格式
 */
interface CountDownTimeWithDays {
  /** 剩余天数（两位数字符串，如 "01"、"10"） */
  days: string;
  /** 剩余小时数 (0-23)（两位数字符串，如 "01"、"23"） */
  hours: string;
  /** 剩余分钟数 (0-59)（两位数字符串，如 "00"、"59"） */
  minutes: string;
  /** 剩余秒数 (0-59)（两位数字符串，如 "00"、"59"） */
  seconds: string;
}

/**
 * 不包含天数的倒计时时间格式
 */
interface CountDownTimeWithoutDays {
  /** 剩余总小时数（两位数字符串，如 "01"、"25"） */
  hours: string;
  /** 剩余分钟数 (0-59)（两位数字符串，如 "00"、"59"） */
  minutes: string;
  /** 剩余秒数 (0-59)（两位数字符串，如 "00"、"59"） */
  seconds: string;
}

/**
 * 倒计时时间类型
 */
export type CountDownTime = CountDownTimeWithDays | CountDownTimeWithoutDays;

/**
 * usePlusCountDown Hook 配置选项
 */
export interface UsePlusCountDownOptions {
  /** 剩余时间（毫秒） */
  milliseconds: number;
  /** 是否包含天数，默认 true。false 时小时数会累加天数 */
  includeDays?: boolean;
  /** 倒计时结束时的回调函数 */
  onComplete?: () => void;
  /** 是否自动开始倒计时，默认 true */
  autoStart?: boolean;
  /** 更新间隔（毫秒），默认 1000ms */
  interval?: number;
  /** 是否启用倒计时，默认 true。为 false 时停止计时 */
  enable?: boolean;
}

/**
 * usePlusCountDown Hook 返回值
 */
export interface UsePlusCountDownReturn {
  /** 格式化的倒计时时间对象 */
  time: CountDownTime;
  /** 剩余毫秒数 */
  remainingMs: number;
  /** 是否已完成 */
  isComplete: boolean;
  /** 是否正在运行 */
  isRunning: boolean;
  /** 开始倒计时 */
  start: () => void;
  /** 暂停倒计时 */
  pause: () => void;
  /** 重置倒计时 */
  reset: (newMilliseconds?: number) => void;
}

/**
 * 格式化数字为两位数字符串
 * @param num 要格式化的数字
 * @returns 两位数字符串（如 "01"、"23"）
 */
const padZero = (num: number): string => {
  return Math.max(0, num).toString().padStart(2, "0");
};

/**
 * 将毫秒转换为时间对象（包含天数）
 */
const convertToTimeWithDays = (ms: number): CountDownTimeWithDays => {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days: padZero(days),
    hours: padZero(hours),
    minutes: padZero(minutes),
    seconds: padZero(seconds),
  };
};

/**
 * 将毫秒转换为时间对象（不包含天数）
 */
const convertToTimeWithoutDays = (ms: number): CountDownTimeWithoutDays => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    hours: padZero(hours),
    minutes: padZero(minutes),
    seconds: padZero(seconds),
  };
};

/**
 * 通用倒计时 Hook
 *
 * 提供灵活的倒计时功能，支持自定义时间格式和控制选项
 *
 * @example
 * ```tsx
 * // 包含天数的倒计时
 * const { time, isComplete, remainingMs } = usePlusCountDown({
 *   milliseconds: 86400000, // 24小时
 *   includeDays: true,
 *   onComplete: () => console.log('倒计时结束')
 * });
 *
 * return (
 *   <div>
 *     {!isComplete && (
 *       <div>
 *         {'days' in time && <span>{time.days}天 </span>}
 *         <span>{time.hours}时 </span>
 *         <span>{time.minutes}分 </span>
 *         <span>{time.seconds}秒</span>
 *       </div>
 *     )}
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // 不包含天数的倒计时（小时数会累加）
 * const { time, start, pause, reset, isRunning } = usePlusCountDown({
 *   milliseconds: 90000000, // 25小时
 *   includeDays: false,
 *   autoStart: false
 * });
 *
 * return (
 *   <div>
 *     <div>{time.hours}:{time.minutes}:{time.seconds}</div>
 *     {!isRunning ? (
 *       <button onClick={start}>开始</button>
 *     ) : (
 *       <button onClick={pause}>暂停</button>
 *     )}
 *     <button onClick={() => reset()}>重置</button>
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // 使用 enable 控制倒计时
 * const [enabled, setEnabled] = useState(true);
 * const { time, isRunning } = usePlusCountDown({
 *   milliseconds: 60000, // 1分钟
 *   enable: enabled,
 *   onComplete: () => console.log('倒计时结束')
 * });
 *
 * return (
 *   <div>
 *     <div>{time.hours}:{time.minutes}:{time.seconds}</div>
 *     <button onClick={() => setEnabled(!enabled)}>
 *       {enabled ? '停止' : '启用'}
 *     </button>
 *   </div>
 * );
 * ```
 */
export const usePlusCountDown = (
  options: UsePlusCountDownOptions,
): UsePlusCountDownReturn => {
  const {
    milliseconds: initialMilliseconds,
    includeDays = true,
    onComplete,
    autoStart = true,
    interval = 1000,
    enable = true,
  } = options;

  // 防御性检查：确保 initialMilliseconds 是有效数字
  const safeInitialMs = useMemo(() => {
    if (
      initialMilliseconds === undefined ||
      initialMilliseconds === null ||
      isNaN(initialMilliseconds) ||
      !isFinite(initialMilliseconds)
    ) {
      return 0;
    }
    return Math.max(0, initialMilliseconds);
  }, [initialMilliseconds]);

  // 使用 ref 存储目标结束时间和回调，避免依赖变化导致定时器重置
  const endTimeRef = useRef<number>(Date.now() + safeInitialMs);
  const onCompleteRef = useRef(onComplete);
  const initialMsRef = useRef(safeInitialMs);
  const prevEnableRef = useRef(enable);

  // 更新 ref
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // 状态管理
  const [remainingMs, setRemainingMs] = useState(safeInitialMs);
  const [isRunning, setIsRunning] = useState(
    autoStart && safeInitialMs > 0 && enable,
  );
  const [isComplete, setIsComplete] = useState(safeInitialMs <= 0);

  // 转换为时间对象
  const time: CountDownTime = includeDays
    ? convertToTimeWithDays(remainingMs)
    : convertToTimeWithoutDays(remainingMs);

  // 开始倒计时
  const start = useCallback(() => {
    if (!isComplete && !isRunning && enable) {
      // 重新计算结束时间
      endTimeRef.current = Date.now() + remainingMs;
      setIsRunning(true);
    }
  }, [isComplete, isRunning, remainingMs, enable]);

  // 暂停倒计时
  const pause = useCallback(() => {
    setIsRunning(false);
  }, []);

  // 重置倒计时
  const reset = useCallback((newMilliseconds?: number) => {
    const resetMs = Math.max(0, newMilliseconds ?? initialMsRef.current);
    endTimeRef.current = Date.now() + resetMs;
    setRemainingMs(resetMs);
    setIsComplete(resetMs <= 0);
    setIsRunning(false);
  }, []);

  // 监听 enable 变化
  useEffect(() => {
    const prevEnable = prevEnableRef.current;

    // 当 enable 从 false 变为 true 时，如果 autoStart 为 true 且倒计时未完成，自动开始
    if (!prevEnable && enable && autoStart && !isComplete && remainingMs > 0) {
      endTimeRef.current = Date.now() + remainingMs;
      setIsRunning(true);
    }

    // 当 enable 为 false 时停止计时
    if (!enable && isRunning) {
      setIsRunning(false);
    }

    // 更新 prevEnableRef
    prevEnableRef.current = enable;
  }, [enable, isRunning, autoStart, isComplete, remainingMs]);

  // 倒计时更新逻辑 - 只依赖 isRunning 和 interval
  useEffect(() => {
    if (!isRunning || isComplete || !enable) {
      return;
    }

    const updateCountdown = () => {
      const now = Date.now();
      const newRemainingMs = Math.max(0, endTimeRef.current - now);

      setRemainingMs(newRemainingMs);

      // 检查是否完成
      if (newRemainingMs <= 0) {
        setIsComplete(true);
        setIsRunning(false);

        // 调用完成回调
        if (onCompleteRef.current) {
          onCompleteRef.current();
        }
      }
    };

    // 立即更新一次
    updateCountdown();

    // 设置定时器
    const timer = setInterval(updateCountdown, interval);

    return () => clearInterval(timer);
  }, [isRunning, isComplete, interval, enable]);

  // 监听外部 milliseconds 变化：仅当与上次应用值差异超过阈值时才同步，避免
  useEffect(() => {
    // 计算新的目标结束时间
    const now = Date.now();
    const newEndTime = now + safeInitialMs;

    // 检查目标结束时间是否发生了显著变化（容差 100ms）
    // 这可以防止当父组件传入动态的 remainingTime (如 target - Date.now()) 时导致的无限重渲染循环
    const isEndTimeChanged = Math.abs(newEndTime - endTimeRef.current) > 100;

    // 总是更新 initialMsRef，以便 reset() 能使用最新的 props
    if (safeInitialMs !== initialMsRef.current) {
      initialMsRef.current = safeInitialMs;
    }

    if (isEndTimeChanged) {
      // 直接执行状态更新，避免调用 reset 导致循环依赖
      const resetMs = Math.max(0, safeInitialMs);
      endTimeRef.current = newEndTime;
      setRemainingMs(resetMs);
      setIsComplete(resetMs <= 0);

      // 根据 autoStart 和 enable 决定是否自动开始
      if (autoStart && resetMs > 0 && enable) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeInitialMs, autoStart, enable]);

  return {
    time,
    remainingMs,
    isComplete,
    isRunning,
    start,
    pause,
    reset,
  };
};
